import { App, Checkbox, Col, Form, Row, Select } from "antd"
import { deleteField } from "firebase/firestore"
import { motion } from "motion/react"
import { useEffect, useMemo, useState } from "react"

import { classSessionAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import Modal from "#blocks/Modal"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { invariant } from "#customTypes/types"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import { useSomeDocs, useStableIds } from "#hooks/jotai/useSubscription"
import {
  useGetOneUser,
  useGetSomeUsers,
  useGetTeachers,
} from "#hooks/newHooks/useGetUser"
import useOptimizedFilter from "#hooks/newHooks/useOptimizedFilter"
import { useInvalidateCheckUsage } from "#hooks/reactQuery/useCheckUsage"
import { useFirebaseBatchUpdate } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"
import { useErrorModal } from "#hooks/useErrorModal"
import { buildAttendanceUpdate } from "#utils/classHelpers"
import {
  getBlankAttendance,
  getNewAttendanceForClass,
} from "#utils/studentHelpers"
import { ErrorType } from "#utils/textFormatters"

import ClassSessionSelectionGrid from "../ClassSessionSelectionGrid"
import { useSessionSelectionState } from "../useSessionSelectionState"

const AddTeacherToClassForm = ({
  sessionIds,
  onClose,
  open,
  teacherId = null,
}: {
  sessionIds: string[]
  onClose: () => void
  open: boolean
  teacherId?: string | null
}) => {
  const isEdit = !!teacherId
  const { trackTeacherEnrollmentAction } = useAnalytics()

  const form = Form.useFormInstance()
  const className = Form.useWatch<string>("name", form)
  const classId = Form.useWatch<string>("id", form)
  // Form.useWatch doesn't work on first render to set default state.
  // We don't need render updates when default changes because this modal closes on submit.
  const defaultTeacherId = form.getFieldValue("defaultTeacherId")

  const teachers = useGetTeachers()

  const teacherOptions = useMemo(() => {
    if (!teachers) return []

    return teachers
      ?.map((teacher) => ({ label: teacher.name, value: teacher.id }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [teachers])

  const { filteredItems: filteredTeacherOptions, onSearch } =
    useOptimizedFilter(teacherOptions)

  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    teacherId
  )
  const selectedTeacher = useGetOneUser(selectedTeacherId)

  const [isDefaultTeacher, setIsDefaultTeacher] = useState(
    !defaultTeacherId || defaultTeacherId === selectedTeacherId
  )

  // Recalculate isDefaultTeacher when selectedTeacherId changes in case they select the existing default
  useEffect(() => {
    setIsDefaultTeacher(
      !defaultTeacherId || defaultTeacherId === selectedTeacherId
    )
  }, [defaultTeacherId, selectedTeacherId])

  const stableSessionIds = useStableIds(sessionIds)
  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )
  const classSessionsFulfilled = useIsFulfilled(stableSessionIds, classSessions)

  const attendanceForClass = useMemo(() => {
    if (!stableSessionIds.length) return []

    const blankAttendance = getBlankAttendance(stableSessionIds)
    const attendance = selectedTeacher?.teacherAttendance?.[classId]

    return blankAttendance.map((session, i) => ({
      ...session,
      status:
        attendance?.[i]?.status &&
        attendance[i].status !== ATTENDANCE_STATUSES.INVALID
          ? attendance?.[i]?.status
          : session.status,
    }))
  }, [selectedTeacher, classId, stableSessionIds])

  const [selectedSessions, setSelectedSessions] = useState<boolean[]>([])

  useEffect(() => {
    setSelectedSessions(
      attendanceForClass?.map(
        ({ status }) =>
          status &&
          status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
          status !== ATTENDANCE_STATUSES.INVALID
      )
    )
  }, [attendanceForClass])

  const { newlySelectedSessions, droppedSessions } = useSessionSelectionState(
    selectedSessions,
    attendanceForClass
  )

  // Build session metadata with disabled reasons
  const sessionMetadata = useMemo(() => {
    return attendanceForClass.map((attendance) => {
      const isDisabled =
        attendance.status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
        attendance.status !== ATTENDANCE_STATUSES.ENROLLED

      return {
        disabledReason: isDisabled
          ? "Session has already been attended"
          : undefined,
      }
    })
  }, [attendanceForClass])

  // Get teachers currently in the class (from sessions)
  const teacherIdsInClass = useMemo(() => {
    return [
      ...new Set(classSessions.flatMap((session) => session.teacherIds ?? [])),
    ].filter(Boolean)
  }, [classSessions])

  const stableRemainingTeacherIds = useStableIds(
    teacherIdsInClass.filter((id) => id !== selectedTeacherId)
  )
  const remainingTeachers = useGetSomeUsers(stableRemainingTeacherIds)

  // Check if we're removing the default teacher
  const isRemovingDefaultTeacher = useMemo(() => {
    if (!selectedTeacherId || !defaultTeacherId) return false
    // Check if all sessions will be deselected (teacher being removed)
    const willBeRemoved = selectedSessions.every((selected) => !selected)
    return willBeRemoved && defaultTeacherId === selectedTeacherId
  }, [selectedSessions, defaultTeacherId, selectedTeacherId])

  // Options for remaining teachers dropdown
  const remainingTeacherOptions = useMemo(() => {
    if (!remainingTeachers) return []
    return remainingTeachers
      .map((teacher) => ({ label: teacher.name, value: teacher.id }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [remainingTeachers])

  const [newDefaultTeacherId, setNewDefaultTeacherId] = useState<string | null>(
    null
  )

  // Reset newDefaultTeacherId when modal closes or condition changes
  useEffect(() => {
    if (!open || !isRemovingDefaultTeacher) {
      setNewDefaultTeacherId(null)
    } else if (isRemovingDefaultTeacher) {
      setNewDefaultTeacherId(remainingTeacherOptions[0]?.value ?? null)
    }
  }, [open, isRemovingDefaultTeacher, remainingTeacherOptions])

  const { message } = App.useApp()

  const { mutate: updateClassSessionAttendance } = useFirebaseBatchUpdate([
    DOC_TYPES.CLASS_SESSIONS,
    DOC_TYPES.CLASSES,
    DOC_TYPES.USERS,
  ])

  const [isPending, setIsPending] = useState(false)

  const recalculateClassUsage = useInvalidateCheckUsage(
    DOC_TYPES.CLASSES,
    classId
  )
  const openErrorModal = useErrorModal("adding teacher to class")

  const handleSubmit = async () => {
    try {
      setIsPending(true)
      await form.validateFields()
      invariant(selectedTeacherId != null, "Teacher ID not found")
      invariant(selectedTeacher != null, "Teacher not found")
      invariant(classSessionsFulfilled, "Sessions not found")

      // Validate that if removing default teacher with other teachers present, a new default must be selected
      const willBeRemoved = selectedSessions.every((selected) => !selected)
      if (
        willBeRemoved &&
        defaultTeacherId === selectedTeacherId &&
        remainingTeacherOptions.length > 0 &&
        !newDefaultTeacherId
      ) {
        throw new Error(
          "Please select a new default teacher before removing the current default teacher."
        )
      }

      const selectedSessionsWithIds = selectedSessions.map((isSelected, i) => {
        return {
          isSelected,
          sessionId: classSessions[i].id,
        }
      })

      const newAttendanceForClass = getNewAttendanceForClass(
        attendanceForClass,
        selectedSessionsWithIds
      )

      const {
        person,
        class: classData,
        classSessionsById,
      } = buildAttendanceUpdate(
        newAttendanceForClass,
        selectedSessionsWithIds,
        classId,
        selectedTeacherId,
        "teacher",
        selectedTeacher?.teacherAttendance
      )

      // Teacher is being removed if newAttendanceForClass is null
      const isTeacherBeingRemoved = newAttendanceForClass === null
      const isRemovingDefault =
        isTeacherBeingRemoved && defaultTeacherId === selectedTeacherId

      let finalDefaultTeacherId: string | null = null
      if (isRemovingDefault) {
        // If removing default teacher and a new default was selected, use it
        if (newDefaultTeacherId) {
          classData.defaultTeacherId = newDefaultTeacherId
          finalDefaultTeacherId = newDefaultTeacherId
        } else {
          // Otherwise clear defaultTeacherId
          classData.defaultTeacherId = deleteField()
          finalDefaultTeacherId = null
        }
      } else if (isDefaultTeacher) {
        // Set as default only if not being removed
        classData.defaultTeacherId = selectedTeacherId
        finalDefaultTeacherId = selectedTeacherId
      }

      // Perform the update - Jotai subscriptions will automatically update atoms
      await updateClassSessionAttendance({
        [DOC_TYPES.USERS]: {
          [selectedTeacherId]: person,
        },
        [DOC_TYPES.CLASSES]: {
          [classId]: classData,
        },
        [DOC_TYPES.CLASS_SESSIONS]: classSessionsById,
      })

      form.setFieldValue("defaultTeacherId", finalDefaultTeacherId)

      trackTeacherEnrollmentAction(ANALYTICS_EVENTS.TEACHER_ADDED_TO_CLASS, {
        teacherId: selectedTeacherId,
        classId: classId,
        sessionCount: selectedSessions.filter(Boolean).length,
      })

      // Invalidate usage query (now uses Jotai atoms)
      recalculateClassUsage()

      void message.success(`${selectedTeacher.name}'s attendance updated.`)

      onClose()
    } catch (error) {
      openErrorModal(
        error as ErrorType,
        `Class: ${className} (${classId})
Teacher: ${selectedTeacher?.name || "Unknown"} (${selectedTeacherId || "Unknown"})
Default Teacher: ${isDefaultTeacher ? "Yes" : "No"}`
      )
    } finally {
      setIsPending(false)
    }
  }

  if (!open) return null

  return (
    <motion.div
      initial={{ width: 600 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ overflow: "hidden" }}
    >
      <Modal
        title={
          isEdit
            ? `Edit teacher in ${className}`
            : `Add teacher to ${className}`
        }
        onClose={onClose}
        open={open}
        width={600}
        onOk={handleSubmit}
        loading={isPending}
      >
        {/* Header */}
        <Row style={{ margin: "20px 0" }}>
          <Col span={24}>
            <Row>
              {isEdit ? (
                <Flex>
                  <h2>{selectedTeacher?.name}</h2>
                </Flex>
              ) : (
                <Select
                  showSearch
                  filterOption={() => true} // Need this for our fuzzy search to work
                  onSearch={onSearch}
                  style={{ width: 300, marginTop: 10 }}
                  value={selectedTeacherId}
                  onChange={setSelectedTeacherId}
                  placeholder="Select teacher"
                  options={
                    filteredTeacherOptions as {
                      key: string
                      label: string
                      value: string
                    }[]
                  }
                  allowClear
                />
              )}
            </Row>
            <DisabledFieldTooltip
              disabled={isDefaultTeacher}
              tooltip="To change the default, please select a different teacher."
            >
              <Checkbox
                style={{
                  // It looks better just a bit bigger
                  transform: `scale(1.1)`,
                  transformOrigin: "top left",
                  userSelect: "none",
                  marginTop: 14,
                }}
                checked={isDefaultTeacher}
                disabled={isDefaultTeacher}
                onChange={() => {
                  setIsDefaultTeacher((prev) => !prev)
                }}
              >
                Set as default for evaluations
              </Checkbox>
            </DisabledFieldTooltip>
            {isRemovingDefaultTeacher && (
              <>
                {remainingTeacherOptions.length > 0 ? (
                  <Flex col gap={8} style={{ marginTop: 16, width: 300 }}>
                    <Form.Item
                      label="Default Teacher"
                      rules={[
                        {
                          required: true,
                          message: "Please select a new default teacher",
                        },
                      ]}
                    >
                      <Select
                        style={{ width: 300 }}
                        value={newDefaultTeacherId}
                        onChange={setNewDefaultTeacherId}
                        placeholder="Select a new default teacher"
                        options={remainingTeacherOptions}
                      />
                    </Form.Item>
                  </Flex>
                ) : (
                  <Flex col gap={8} style={{ marginTop: 16 }}>
                    <div style={{ color: "red", fontWeight: 500 }}>
                      Warning: This will leave the class with no teacher.
                    </div>
                  </Flex>
                )}
              </>
            )}
          </Col>
        </Row>

        <Row
          gutter={40}
          style={{ transition: "all 0.3s ease-out", flexWrap: "nowrap" }}
        >
          {/* Sessions */}
          <Col
            span={24}
            style={{ transition: "all 0.3s ease-out", maxWidth: 580 }}
          >
            <Row>
              <Col span={24}>
                {selectedTeacher ? (
                  <ClassSessionSelectionGrid
                    attendanceForClass={attendanceForClass}
                    classSessions={classSessions}
                    selectedSessions={selectedSessions}
                    setSelectedSessions={setSelectedSessions}
                    disabledMessage="Sessions that the teacher attended can not be changed."
                    sessionMetadata={sessionMetadata}
                    newlySelectedSessions={newlySelectedSessions}
                    droppedSessions={droppedSessions}
                  />
                ) : (
                  <Flex col gap={10}>
                    <h2>Sessions:</h2>
                    <Flex>
                      <h2>No teacher selected</h2>
                    </Flex>
                  </Flex>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </motion.div>
  )
}

export default AddTeacherToClassForm
